import { useMemo, useState } from "react";
import { useModal } from "react-modal-hook";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  GetWorkflowQuery,
  useUpdateWorkflowStepCollaboratorsMutation,
  useWorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { CommonModal, useModalNavigation } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import { Collaborator, InheritedPermissionGroup } from "../Common/ShareForm";
import {
  InheritedPermissionsDetail,
  mapGroupPermissionsFromStep,
  mapIndividualCollaboratorsFromStep,
  WorkflowStepShareForm,
} from "./ShareWorkflowStep";

type ModalView = "main" | "workflowPermissions";

interface InheritedPermissionsViewProps {
  entityName: string;
  group: InheritedPermissionGroup;
  onBack: () => void;
  onClose: () => void;
}

const InheritedPermissionsView = ({
  entityName,
  group,
  onBack,
  onClose,
}: InheritedPermissionsViewProps) => {
  return (
    <CommonModal
      title="Share workflow step"
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      handlePrevious={onBack}
      content={<InheritedPermissionsDetail group={group} />}
    />
  );
};

export interface ShareWorkflowStepModalProps {
  workflow: Pick<NonNullable<GetWorkflowQuery["workflow"]>, "id">;
  workflowStep: Pick<
    NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
    "id" | "name"
  >;
  onClose: () => void;
}

export const ShareWorkflowStepModalContent = ({
  workflowStep,
  workflow,
  onClose,
}: ShareWorkflowStepModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { view, goTo, goBack } = useModalNavigation<ModalView>("main");
  const [selectedInheritedGroup, setSelectedInheritedGroup] =
    useState<InheritedPermissionGroup | null>(null);

  const { data: stepData, loading: isLoading } =
    useWorkflowStepCollaboratorsQuery({
      variables: { workflowStepId: workflowStep.id },
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const [updateStepCollaborators, { loading: isSubmitting }] =
    useUpdateWorkflowStepCollaboratorsMutation();

  const collaboratorsData = useMemo(
    () => stepData?.workflowStep?.collaborators ?? [],
    [stepData?.workflowStep?.collaborators],
  );

  const initialCollaborators = useMemo(
    () => mapIndividualCollaboratorsFromStep(collaboratorsData),
    [collaboratorsData],
  );

  const groupPermissions = useMemo(
    () => mapGroupPermissionsFromStep(collaboratorsData),
    [collaboratorsData],
  );

  const [currentCollaborators, setCurrentCollaborators] =
    useState<Maybe<Collaborator[]>>(null);

  const handleSubmit = async () => {
    try {
      await updateStepCollaborators({
        variables: {
          data: {
            workflowId: workflow.id,
            workflowStepId: workflowStep.id,
            collaborators: (currentCollaborators ?? initialCollaborators).map(
              c => ({
                entityId: c.id,
                entityType: AccessControlEntityType.PUBLIC_USER,
                policy: c.permission as AccessControlPolicy,
              }),
            ),
          },
        },
      });

      track("Shared Workflow Step", {
        workflowId: workflow.id,
        workflowStepId: workflowStep.id,
      });

      addSuccessToast(`This step has been updated successfully!`);
      onClose();
    } catch {
      addErrorToast(`Failed to update step`);
    }
  };

  const handleInheritedPermissionGroupClick = (
    group: InheritedPermissionGroup,
  ) => {
    setSelectedInheritedGroup(group);
    goTo("workflowPermissions");
  };

  if (view === "workflowPermissions" && selectedInheritedGroup) {
    return (
      <InheritedPermissionsView
        entityName={workflowStep.name}
        group={selectedInheritedGroup}
        onBack={goBack}
        onClose={onClose}
      />
    );
  }

  return (
    <CommonModal
      title="Share workflow step"
      subtitle={workflowStep.name}
      size="large"
      onCancel={onClose}
      primaryButtonText={isSubmitting ? "Saving..." : "Invite"}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading}
      secondaryButtons={[
        {
          text: "Done",
          onClick: onClose,
        },
      ]}
      content={
        <WorkflowStepShareForm
          collaborators={currentCollaborators ?? initialCollaborators}
          inheritedPermissions={groupPermissions}
          onStateChange={setCurrentCollaborators}
          onInheritedPermissionGroupClick={handleInheritedPermissionGroupClick}
          isLoading={isLoading}
        />
      }
    />
  );
};

export const useShareWorkflowStepModal = ({
  workflowStep,
  workflow,
}: Omit<ShareWorkflowStepModalProps, "onClose">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowStepModalContent
          workflowStep={workflowStep}
          workflow={workflow}
          onClose={hideModal}
        />
      </Modal>
    ),
    [workflowStep, workflow],
  );

  return [showModal, hideModal] as const;
};
